import { useEffect, useState } from "react";
import type { Rpc } from "../lib/rpc";

const PLATFORM_OPTIONS = [
  "",            // General — no platform context
  "Reddit", "Twitter", "OnlyFans", "Fansly", "ManyVids", "RedGifs", "FetLife",
  "Text Only",   // No media, generates from instructions only
];

interface ContentFile { name: string; path: string; size: number; isVideo: boolean }
interface CaptionResult { description?: string; title?: string; body?: string; error?: string }

export function CaptioningPage({ rpc, electronConnected }: { rpc: Rpc; electronConnected: boolean }) {
  const [sourceTab, setSourceTab] = useState<"catalog" | "queued" | "manual">("catalog");
  const [files, setFiles] = useState<ContentFile[]>([]);
  const [filter, setFilter] = useState("");
  const [selectedPath, setSelectedPath] = useState("");
  const [manualPath, setManualPath] = useState("");
  const [instructions, setInstructions] = useState("");
  const [platform, setPlatform] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CaptionResult | null>(null);

  // Fetch file list when source tab changes (catalog/queued).
  useEffect(() => {
    if (!electronConnected || sourceTab === "manual") return;
    let cancelled = false;
    rpc.call<{ ok: boolean; files?: ContentFile[] }>("get-content-files", sourceTab)
      .then((r) => { if (!cancelled && r.ok) setFiles(r.files || []); });
    return () => { cancelled = true; };
  }, [rpc, electronConnected, sourceTab]);

  const path = sourceTab === "manual" ? manualPath : selectedPath;
  const isTextOnly = platform === "Text Only";
  const canRun = (isTextOnly ? instructions.trim().length > 0 : path.trim().length > 0) && !busy;

  async function runCaption() {
    setBusy(true);
    setResult(null);
    try {
      const r = await rpc.call<CaptionResult>("caption-file", path, instructions, platform);
      setResult(r || { error: "no response" });
    } catch (e) {
      setResult({ error: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  if (!electronConnected) {
    return (
      <section className="settings-page">
        <header className="settings-page-header"><h2>✍️ Captioning</h2></header>
        <div className="empty">Electron must be running on the worker PC.</div>
      </section>
    );
  }

  const visibleFiles = files.filter((f) =>
    !filter || f.name.toLowerCase().includes(filter.toLowerCase())
  ).slice(0, 200);

  return (
    <section className="settings-page">
      <header className="settings-page-header">
        <h2>✍️ Captioning</h2>
      </header>

      <div className="caption-controls">
        <div className="caption-row">
          <label>Source</label>
          <div className="caption-source-tabs">
            {(["catalog", "queued", "manual"] as const).map((s) => (
              <button
                key={s}
                className={`caption-source-tab ${sourceTab === s ? "active" : ""}`}
                onClick={() => setSourceTab(s)}
              >
                {s === "catalog" ? "📁 Catalog" : s === "queued" ? "📋 Queued" : "✏️ Manual path"}
              </button>
            ))}
          </div>
        </div>

        {sourceTab !== "manual" && (
          <>
            <div className="caption-row">
              <label>Filter</label>
              <input
                type="text"
                placeholder="filter filenames…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
            <div className="caption-file-list">
              {visibleFiles.map((f) => (
                <button
                  key={f.path}
                  className={`caption-file-row ${selectedPath === f.path ? "active" : ""}`}
                  onClick={() => setSelectedPath(f.path)}
                  title={f.path}
                >
                  <span className="caption-file-icon">{f.isVideo ? "🎬" : "🖼️"}</span>
                  <span className="caption-file-name">{f.name}</span>
                </button>
              ))}
              {visibleFiles.length === 0 && (
                <div className="empty small">No files in {sourceTab}.</div>
              )}
            </div>
          </>
        )}

        {sourceTab === "manual" && (
          <div className="caption-row">
            <label>File path on worker PC</label>
            <input
              type="text"
              placeholder="C:\Users\…\some\file.mp4"
              value={manualPath}
              onChange={(e) => setManualPath(e.target.value)}
              spellCheck={false}
            />
          </div>
        )}

        <div className="caption-row">
          <label>Platform context</label>
          <select value={platform} onChange={(e) => setPlatform(e.target.value)}>
            {PLATFORM_OPTIONS.map((p) => (
              <option key={p || "general"} value={p}>
                {p || "General (no platform)"}
              </option>
            ))}
          </select>
        </div>

        <div className="caption-row">
          <label>Custom instructions {isTextOnly && <span className="muted">(required for Text Only)</span>}</label>
          <textarea
            rows={3}
            placeholder='e.g. "make it funny", "mention the outfit", or for Text Only: a full prompt'
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
          />
        </div>

        <div className="caption-row caption-actions">
          <button className="primary" onClick={runCaption} disabled={!canRun}>
            {busy ? "Captioning…" : "▶ Generate Caption"}
          </button>
          {!isTextOnly && path && <span className="muted small">{path}</span>}
        </div>
      </div>

      {result && (
        <div className="caption-result">
          {result.error ? (
            <div className="err">Error: {result.error}</div>
          ) : (
            <>
              {result.description && (
                <div className="result-block">
                  <div className="result-label">📝 Description</div>
                  <pre className="result-text">{result.description}</pre>
                </div>
              )}
              {result.title && (
                <div className="result-block">
                  <div className="result-label">🏷️ Title</div>
                  <div className="result-title">{result.title}</div>
                </div>
              )}
              {result.body && (
                <div className="result-block">
                  <div className="result-label">💬 Body</div>
                  <div className="result-body">{result.body}</div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
